import { cn } from "@/utils";
import {
  buildBlockPrepareOptions,
  HtmlBlockShield,
  prepareSrcDocHtml,
  useThemeContext,
} from "@cohesive/ui-components";
import { memo, useMemo } from "react";
import type { HtmlStreamPreview } from "../context/CanvasHtmlStreamPreviewContext";

/**
 * Live paint-in preview shown inside a loading ghost while the agent is still
 * streaming an HTML block's content. Renders the partial document through the
 * same preparation pipeline the finished block uses, so the handoff to the
 * real card is visually seamless rather than a reflow.
 *
 * Display-only: the block runtime is deliberately NOT injected. A partial
 * document's scripts are half-written, and there is nothing to interact with
 * or edit until the real node lands.
 *
 * The iframe reloads on each throttled emit (a srcDoc swap); partial HTML is
 * parse-tolerant, and the throttle in CanvasHtmlStreamPreviewContext keeps
 * that cadence low enough to read as painting rather than flickering.
 */
export const HtmlBlockStreamingGhost = memo(function HtmlBlockStreamingGhost({
  preview,
  width,
  height,
  title,
  selected,
}: {
  preview: HtmlStreamPreview;
  width: number;
  height?: number;
  title?: string;
  selected?: boolean;
}) {
  const { mode } = useThemeContext();
  const theme = mode === "dark" ? "dark" : "light";
  const frameless = preview.presentation?.frameless === true;
  const transparent = preview.presentation?.transparent === true;

  const srcDoc = useMemo(
    () =>
      prepareSrcDocHtml(
        preview.html,
        buildBlockPrepareOptions({ theme, transparent, runtime: false }),
      ),
    [preview.html, theme, transparent],
  );

  return (
    <div
      className={cn(
        "relative overflow-hidden",
        !frameless && "rounded-xl",
        !frameless && !transparent && "bg-card",
        selected && "ring-2 ring-canvas-selection/60",
      )}
      style={{ width, height, colorScheme: theme }}
    >
      <iframe
        srcDoc={srcDoc}
        className="h-full w-full border-0"
        style={{
          pointerEvents: "none",
          colorScheme: theme,
          background: "transparent",
        }}
        sandbox="allow-scripts"
        title={title || "HTML block (writing)"}
      />
      {/* Host-DOM hit plate — keeps wheel/drag panning working over the
          streaming preview frame (see HtmlBlockShield). */}
      <HtmlBlockShield />
      <div className="absolute bottom-2 right-2 z-10 animate-pulse rounded-full bg-neutral-900/70 px-2 py-0.5 text-[10px] font-medium text-neutral-0">
        writing…
      </div>
    </div>
  );
});
